import React, { useState, useEffect } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import { useCatchResponse } from '../../utils/useCatchResponse';

const categories = [
    { label: 'All', value: 'ALL', icon: 'pi pi-th-large', color: '#1c515e', desc: 'Browse all learning resources' },
    { label: 'Syllabus', value: 'Syllabus', icon: 'pi pi-book', color: '#14452f', desc: 'Course syllabus & curriculum outline' },
    { label: 'Resources', value: 'Resources', icon: 'pi pi-folder-open', color: '#2f6fed', desc: 'Reference notes & study material' },
    { label: 'Question Paper', value: 'QuestionPaper', icon: 'pi pi-file-edit', color: '#B1007E', desc: 'Previous & sample question papers' },
    { label: 'Assignments', value: 'Assignments', icon: 'pi pi-pencil', color: '#F5A623', desc: 'Assignments & homework' },
];
const categoryLabel = { ALL: 'All', Syllabus: 'Syllabus', Resources: 'Resources', QuestionPaper: 'Question Paper', Assignments: 'Assignments' };
const catMeta = (v) => categories.find(c => c.value === v) || {};
const viewOptions = [
    { label: 'Category', value: 'category' },
    { label: 'Course/Class Wise', value: 'class' },
];

const LearningResources = () => {
    const { catchResponse } = useCatchResponse();
    const [view, setView] = useState('category');
    const [category, setCategory] = useState(null);

    const [schoolId, setSchoolId] = useState('');
    const [userId, setUserId] = useState('');
    const [userRole, setUserRole] = useState('');
    const [studentId, setStudentId] = useState('');
    const [financialYear, setFinancialYear] = useState('');

    const [year, setYear] = useState(null);
    const [course, setCourse] = useState(null);
    const [consumerClassId, setConsumerClassId] = useState('');
    const [semester, setSemester] = useState(null);
    const [subject, setSubject] = useState(null);
    const [search, setSearch] = useState('');

    const [yearList, setYearList] = useState([]);
    const [classList, setClassList] = useState([]);
    const [termList, setTermList] = useState([]);
    const [subjectList, setSubjectList] = useState([]);
    const [classMap, setClassMap] = useState({});
    const [termMap, setTermMap] = useState({});
    const [subjectMap, setSubjectMap] = useState({});

    const [results, setResults] = useState([]);
    const [searched, setSearched] = useState(false);
    const [mySubmissions, setMySubmissions] = useState({});

    useEffect(() => {
        const init = async () => {
            const loginData = CommonConfig.loginData();
            const uid = loginData?._id;
            const role = loginData?.UserRole || '';
            const sid = CommonConfig.isOrgOwner(role) ? uid : loginData?.CreatedBy;
            const localStudentId = role === 'Parent'
                ? (loginData?.SelectedChildData?._id || '')
                : (role === 'Student' ? uid : '');
            setUserId(uid);
            setSchoolId(sid);
            setUserRole(role);
            setStudentId(localStudentId);

            const years = await CommonConfig.getFinancialYears(sid) || [];
            // Pick a year id that actually exists in the list, else the class list
            // (which needs a valid FinancialYearId) comes back empty and nothing
            // pre-selects. Prefer the saved cookie, then the year whose date range
            // contains today, then the first available.
            const now = new Date();
            const cookieFy = CommonConfig.getCookie('selectedAcademicYear');
            const validYear =
                years.find(y => y._id === cookieFy) ||
                years.find(y => y?.StartDate && y?.EndDate && new Date(y.StartDate) <= now && now <= new Date(y.EndDate)) ||
                years[0] || null;
            const fy = validYear?._id || '';
            setFinancialYear(fy);
            setYear(fy);
            setYearList(years.map(y => ({ value: y._id, label: y.label })));

            // Consumer (Student/Parent): resolve their own class first so the
            // Course/Class list and Subjects can be scoped to just that class.
            let consumerCls = '';
            if (localStudentId) {
                try {
                    const res = await api.post(APIConstant.path.GetLearningResources, {
                        SchoolId: sid, FinancialYearId: fy, StudentId: localStudentId,
                    });
                    consumerCls = res?.resolvedClass ? String(res.resolvedClass) : '';
                    if (consumerCls) { setConsumerClassId(consumerCls); setCourse(consumerCls); }
                } catch (e) { /* non-fatal */ }
            }

            await loadMasters(uid, sid, fy, consumerCls);
        };
        init();
    }, []);

    const loadMasters = async (uid, sid, fy, consumerCls = '') => {
        try {
            const classRes = await api.post(APIConstant.path.getClassList, { UserID: uid, SchoolId: sid, FinancialYearId: fy, ScopeBy: 'Teaching' });
            if (classRes.success) {
                let list = (classRes.data || []).filter(x => x?.Status === 'Active').map(x => ({
                    value: x._id, label: `${x?.ClassName}-${x?.SectionFullName}-${x?.MediumName}`,
                }));
                // Consumer: restrict the Course/Class list to their own class.
                if (consumerCls) list = list.filter(x => x.value === consumerCls);
                setClassList(list);
                const cm = {}; list.forEach(x => { cm[x.value] = x.label; }); setClassMap(cm);
            }
        } catch (err) { console.log(err); }

        try {
            const termRes = await api.post(APIConstant.path.getTermList, { UserID: sid });
            if (termRes.success) {
                const list = (termRes.data || []).map(x => ({ value: x._id, label: x?.label || x?.TermName }));
                setTermList(list);
                const tm = {}; list.forEach(x => { tm[x.value] = x.label; }); setTermMap(tm);
            }
        } catch (err) { console.log(err); }

        try {
            const subjectPayload = consumerCls
                ? { UserID: sid, ClassId: consumerCls }
                : { UserID: sid, TeacherId: uid, ScopeBy: 'Teaching' };
            const subjectRes = await api.post(APIConstant.path.getSubjectList, subjectPayload);
            if (subjectRes.success) {
                const list = (subjectRes.data || []).map(x => ({ value: x._id, label: x?.SubjectName }));
                setSubjectList(list);
                const sm = {}; list.forEach(x => { sm[x.value] = x.label; }); setSubjectMap(sm);
            }
        } catch (err) { console.log(err); }
    };

    const resetFilters = () => {
        setCourse(consumerClassId || null); setSemester(null); setSubject(null); setSearch('');
        setYear(financialYear); setResults([]); setSearched(false);
    };

    const openCategory = (value) => {
        resetFilters();
        setCategory(value);
    };

    const backToCategories = () => {
        resetFilters();
        setCategory(null);
    };

    const handleSearch = async () => {
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetLearningResources, {
                SchoolId: schoolId, FinancialYearId: year || financialYear, Type: category,
                Class: course, Term: semester, Subject: subject, Search: search,
                ...(isConsumer ? { StudentId: studentId } : {}),
            });
            const list = response.success ? (response.data || []) : [];
            setResults(list);
            setSearched(true);
            if (isConsumer) loadMySubmissions(list);
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleClassView = async (selectedClass) => {
        setCourse(selectedClass);
        if (!selectedClass) { setResults([]); setSearched(false); return; }
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.GetLearningResources, {
                SchoolId: schoolId, FinancialYearId: year || financialYear, Class: selectedClass,
                ...(isConsumer ? { StudentId: studentId } : {}),
            });
            const list = response.success ? (response.data || []) : [];
            setResults(list);
            setSearched(true);
            if (isConsumer) loadMySubmissions(list);
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const handleDownloadZip = async (type) => {
        if (!results.length) { Toast.info({ message: 'No files to download for this selection' }); return; }
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.DownloadLearningResourcesZip, {
                SchoolId: schoolId, FinancialYearId: year || financialYear,
                Type: type || category, Class: course, Term: semester, Subject: subject,
                ...(isConsumer ? { StudentId: studentId } : {}),
            });
            Loader.hide();
            if (response?.success && response?.url) {
                window.open(`${apiBase}${response.url}?token=${CommonConfig.fileToken()}`, '_blank');
            } else {
                Toast.error({ message: response?.message || 'Failed to prepare the download' });
            }
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const isConsumer = (userRole === 'Parent' || userRole === 'Student') && !!studentId;

    const loadMySubmissions = async (list) => {
        if (!studentId) return;
        const lmsIds = (list || []).filter(r => r.Type === 'Assignments').map(r => r._id);
        if (!lmsIds.length) { setMySubmissions({}); return; }
        try {
            const res = await api.post(APIConstant.path.GetMySubmissions, { StudentId: studentId, LmsIds: JSON.stringify(lmsIds) });
            const map = {};
            (res?.success ? res.data : []).forEach(s => { map[s.LmsId] = s; });
            setMySubmissions(map);
        } catch (e) { /* non-fatal */ }
    };

    const handleSubmitAssignment = async (row, files) => {
        if (!files || !files.length) return;
        if (row?.DueDate && new Date() > new Date(row.DueDate)) {
            Toast.error({ message: 'Submission deadline has passed' });
            return;
        }
        try {
            Loader.show();
            const formData = new FormData();
            formData.append('LmsId', row._id);
            formData.append('StudentId', studentId);
            formData.append('SchoolId', schoolId);
            formData.append('FinancialYearId', year || financialYear);
            Array.from(files).forEach(f => formData.append('Attachments', f));
            const res = await api.post(APIConstant.path.SubmitAssignment, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            Loader.hide();
            if (res?.success) {
                Toast.success({ message: res.message || 'Assignment submitted' });
                setMySubmissions(prev => ({ ...prev, [row._id]: res.data }));
            } else {
                Toast.error({ message: res?.message || 'Failed to submit' });
            }
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const fmtDeadline = (d) => {
        if (!d) return '';
        const dt = new Date(d);
        return dt.toLocaleDateString('en-GB') + ' ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const handleMyDownload = async () => {
        if (!studentId) { Toast.error({ message: 'No student/class found for this account' }); return; }
        try {
            Loader.show();
            const response = await api.post(APIConstant.path.DownloadMyResourcesZip, {
                SchoolId: schoolId, FinancialYearId: financialYear, Type: 'Syllabus', StudentId: studentId,
            });
            Loader.hide();
            if (response?.success && response?.url) {
                window.open(`${apiBase}${response.url}?token=${CommonConfig.fileToken()}`, '_blank');
            } else {
                Toast.error({ message: response?.message || 'Failed to prepare the download' });
            }
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const mapNames = (ids, map) => (ids || []).map(id => map[id]).filter(Boolean);
    const totalFiles = results.reduce((sum, r) => sum + (r.Attachments?.length || 0), 0);

    const resultCard = (row) => (
        <div key={row._id} className="lr-card">
            <div className="lr-card-head">
                <span className="lr-card-type" style={{ color: catMeta(row.Type).color }}>
                    <i className={catMeta(row.Type).icon || 'pi pi-file'} /> {categoryLabel[row.Type] || row.Type}
                </span>
                <span className="lr-card-files"><i className="pi pi-paperclip" /> {row.Attachments?.length || 0}</span>
            </div>
            <div className="lr-card-name">{row.Name}</div>
            <div className="lr-card-tags">
                {mapNames(row.Class, classMap).map((n, i) => <Tag key={`c${i}`} value={n} />)}
                {mapNames(row.Subject, subjectMap).map((n, i) => <Tag key={`s${i}`} value={n} severity="info" />)}
                {mapNames(row.Term, termMap).map((n, i) => <Tag key={`t${i}`} value={n} severity="warning" />)}
            </div>
            {row.Remarks ? <div className="lr-card-remarks">{row.Remarks}</div> : null}
            <div className="lr-card-files-list">
                {(row.Attachments || []).map((f, i) => (
                    <a key={i} href={`${apiBase}/Upload/LMS/${f}?token=${CommonConfig.fileToken()}`} target="_blank" rel="noopener noreferrer" className="lr-file-chip">
                        <i className="pi pi-file" /> {f.replace(/^\d+-/, '')}
                    </a>
                ))}
            </div>
            {row.Type === 'Assignments' && isConsumer && (() => {
                const sub = mySubmissions[row._id];
                const pastDue = row.DueDate && new Date() > new Date(row.DueDate);
                const graded = sub && (sub.Marks || sub.Grade);
                return (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e0e0e0' }}>
                        {row.DueDate ? (
                            <div style={{ fontSize: 12, color: pastDue ? '#dc2626' : '#64748b', marginBottom: 6 }}>
                                <i className="pi pi-clock" /> Deadline: {fmtDeadline(row.DueDate)}{pastDue ? ' (closed)' : ''}
                            </div>
                        ) : null}
                        {sub?.SubmittedFiles?.length ? (
                            <div style={{ marginBottom: 6 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', marginBottom: 4 }}>
                                    <i className="pi pi-check-circle" /> Submitted
                                </div>
                                {sub.SubmittedFiles.map((f, i) => (
                                    <a key={i} href={`${apiBase}/Upload/Submissions/${f}?token=${CommonConfig.fileToken()}`} target="_blank" rel="noopener noreferrer" className="lr-file-chip">
                                        <i className="pi pi-file" /> {f.replace(/^\d+-/, '')}
                                    </a>
                                ))}
                            </div>
                        ) : null}
                        {graded ? (
                            <div style={{ fontSize: 12, marginBottom: 6, color: '#1c515e' }}>
                                <b>{sub.Grade ? `Grade: ${sub.Grade}` : `Marks: ${sub.Marks}${row.MaxMarks ? ` / ${row.MaxMarks}` : ''}`}</b>
                                {sub.Feedback ? <div style={{ color: '#64748b', marginTop: 2 }}>Remark: {sub.Feedback}</div> : null}
                            </div>
                        ) : null}
                        {!pastDue ? (
                            <label className="lr-file-chip" style={{ cursor: 'pointer', background: '#F5A623', color: '#fff' }}>
                                <i className="pi pi-upload" /> {sub?.SubmittedFiles?.length ? 'Re-submit' : 'Submit Assignment'}
                                <input type="file" hidden multiple onChange={(e) => { handleSubmitAssignment(row, e.target.files); e.target.value = ''; }} />
                            </label>
                        ) : (
                            !sub?.SubmittedFiles?.length ? <div style={{ fontSize: 12, color: '#dc2626' }}>Submission closed</div> : null
                        )}
                    </div>
                );
            })()}
        </div>
    );

    const resultsBlock = (
        <>
            {searched && results.length > 0 && (
                <div className="lr-count">{results.length} item(s) · {totalFiles} file(s)</div>
            )}
            {results.length > 0 ? (
                <div className="lr-results">{results.map(resultCard)}</div>
            ) : (
                <div className="lr-empty">
                    <i className="pi pi-inbox" />
                    {searched ? 'No resources found for the selected filters.' : 'Choose your filters and click Search to view resources.'}
                </div>
            )}
        </>
    );

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <style>{`
                .lr-toolbar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:18px; }
                .lr-cat-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:18px; }
                .lr-cat-card { position:relative; background:#fff; border:1px solid var(--border,#e4e9f2); border-radius:16px; padding:22px; cursor:pointer; overflow:hidden; transition:transform .15s, box-shadow .15s, border-color .15s; }
                .lr-cat-card:hover { transform:translateY(-4px); box-shadow:0 10px 24px rgba(16,24,40,.10); border-color:transparent; }
                .lr-cat-accent { position:absolute; top:0; left:0; width:100%; height:5px; }
                .lr-cat-icon { width:56px; height:56px; border-radius:14px; display:flex; align-items:center; justify-content:center; margin-bottom:14px; }
                .lr-cat-icon i { font-size:24px; color:#fff; }
                .lr-cat-title { font-size:18px; font-weight:800; color:#1e2a44; }
                .lr-cat-desc { font-size:13px; color:#6b7794; margin-top:6px; min-height:34px; }
                .lr-cat-foot { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-top:14px; }
                .lr-cat-go { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:700; }
                .lr-cat-dl { padding:6px 12px !important; font-size:12px !important; }
                .lr-cat-dl.p-button { background:var(--button-bg,#14452f); border-color:var(--button-bg,#14452f); }
                .lr-detail-head { display:flex; align-items:center; gap:14px; margin-bottom:16px; }
                .lr-back { display:inline-flex; align-items:center; gap:6px; background:#fff; border:1px solid var(--border,#e4e9f2); border-radius:8px; padding:8px 14px; cursor:pointer; font-weight:600; color:#1e2a44; font-size:14px; }
                .lr-back:hover { background:#f4f6fb; }
                .lr-detail-title { display:flex; align-items:center; gap:10px; font-size:20px; font-weight:800; color:#1e2a44; }
                .lr-detail-title .lr-dt-icon { width:38px; height:38px; border-radius:10px; display:flex; align-items:center; justify-content:center; }
                .lr-detail-title .lr-dt-icon i { color:#fff; font-size:18px; }
                .lr-filter-card { background:#fff; border:1px solid var(--border,#e4e9f2); border-radius:12px; padding:18px; margin-bottom:18px; }
                .lr-filter-grid { display:grid; grid-template-columns:repeat(3, 1fr); gap:14px; align-items:end; }
                .lr-actions { display:flex; gap:10px; margin-top:16px; flex-wrap:wrap; align-items:center; }
                .lr-download { margin-left:auto; }
                .lr-count { color:#6b7794; font-size:13px; margin-bottom:10px; }
                .lr-results { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:14px; }
                .lr-card { background:#fff; border:1px solid var(--border,#e4e9f2); border-radius:12px; padding:14px; display:flex; flex-direction:column; gap:8px; box-shadow:0 1px 3px rgba(0,0,0,.04); }
                .lr-card-head { display:flex; align-items:center; justify-content:space-between; font-size:12px; color:#6b7794; }
                .lr-card-type { display:flex; align-items:center; gap:6px; font-weight:700; }
                .lr-card-name { font-size:16px; font-weight:700; color:#1e2a44; }
                .lr-card-tags { display:flex; flex-wrap:wrap; gap:6px; }
                .lr-card-remarks { font-size:13px; color:#6b7794; }
                .lr-card-files-list { display:flex; flex-direction:column; gap:6px; margin-top:4px; }
                .lr-file-chip { display:flex; align-items:center; gap:6px; font-size:13px; color:#2f6fed; text-decoration:none; word-break:break-all; }
                .lr-empty { text-align:center; padding:50px 20px; color:#6b7794; }
                .lr-empty i { font-size:42px; display:block; margin-bottom:12px; color:#c3ccdd; }

                /* Styles to make SelectButton look exactly like e-tab-main TabView from Attendance */
                .lr-tab-select.p-selectbutton { display:flex; gap:0.5rem; }
                .lr-tab-select.p-selectbutton .p-button { 
                    border-radius: 6px !important;
                    border: none !important;
                    background-color: rgba(0, 0, 0, 0.05) !important;
                    padding: 0.25rem 0.75rem !important;
                    height: 2rem !important;
                    color: #0f0f0f !important;
                    font-size: 0.875rem !important;
                    font-weight: 600 !important;
                    transition: min-width 0.5s ease;
                    box-shadow: none !important;
                }
                .lr-tab-select.p-selectbutton .p-button:hover,
                .lr-tab-select.p-selectbutton .p-button.p-highlight {
                    background-color: #4e8ba5 !important;
                    color: #FFFFFF !important;
                }
                .lr-tab-select.p-selectbutton .p-button .p-button-label {
                    font-weight: inherit !important;
                    color: inherit !important;
                }
            `}</style>

            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-folder-open"></i>
                    <h2>Learning Resources</h2>
                </div>
                <div className="p-d-flex p-ai-center">
                    <SelectButton value={view} options={viewOptions} className="lr-tab-select"
                        onChange={(e) => { if (e.value) { setView(e.value); setCategory(null); resetFilters(); } }} />
                </div>
            </div>

            {view === 'category' && !category && (
                <div className="lr-cat-grid p-mt-3">
                    {categories.map(c => (
                        <div key={c.value} className="lr-cat-card" onClick={() => openCategory(c.value)}>
                            <div className="lr-cat-accent" style={{ background: c.color }} />
                            <div className="lr-cat-icon" style={{ background: c.color }}><i className={c.icon} /></div>
                            <div className="lr-cat-title">{c.label}</div>
                            <div className="lr-cat-desc">{c.desc}</div>
                            <div className="lr-cat-foot">
                                <span className="lr-cat-go" style={{ color: c.color }}>Browse <i className="pi pi-arrow-right" /></span>
                                {c.value === 'Syllabus' && isConsumer && (
                                    <Button label="Download" icon="pi pi-download" className="lr-cat-dl"
                                        onClick={(e) => { e.stopPropagation(); handleMyDownload(); }} />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {view === 'category' && category && (
                <>
                    <div className="lr-detail-head p-mt-3">
                        <button className="lr-back e-h-btn" onClick={backToCategories}><i className="pi pi-arrow-left" /></button>
                        <div className="lr-detail-title">
                            <span className="lr-dt-icon" style={{ background: catMeta(category).color }}><i className={catMeta(category).icon} /></span>
                            {categoryLabel[category]}
                        </div>
                    </div>

                    <div className="lr-filter-card">
                        <div className="lr-filter-grid">
                            <div className="custom-inputs">
                                <label>Academic Year</label>
                                <Dropdown className="p-w-100 p-mt-1" value={year} options={yearList} onChange={(e) => setYear(e.value)} placeholder="Select Year" filter showClear />
                            </div>
                            <div className="custom-inputs">
                                <label>Course/Class</label>
                                <Dropdown className="p-w-100 p-mt-1" value={course} options={classList} onChange={(e) => setCourse(e.value)} placeholder="All Courses" filter showClear />
                            </div>
                            <div className="custom-inputs">
                                <label>Semester</label>
                                <Dropdown className="p-w-100 p-mt-1" value={semester} options={termList} onChange={(e) => setSemester(e.value)} placeholder="All Semesters" filter showClear />
                            </div>
                            <div className="custom-inputs">
                                <label>Subject</label>
                                <Dropdown className="p-w-100 p-mt-1" value={subject} options={subjectList} onChange={(e) => setSubject(e.value)} placeholder="All Subjects" filter showClear />
                            </div>
                            <div className="custom-inputs">
                                <label>Search</label>
                                <InputText className="p-w-100 p-mt-1" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name" onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                            </div>
                        </div>
                        <div className="lr-actions">
                            <Button label="Search" icon="pi pi-search" className="btn-text-action-primary" onClick={handleSearch} />
                            <Button label="Reset" icon="pi pi-refresh" className="btn-text-action-secondary" onClick={resetFilters} />
                            <Button label={category === 'ALL' ? 'Download All Resources' : `Download Your ${categoryLabel[category]}`} icon="pi pi-download" className="btn-text-action-primary lr-download"
                                disabled={!results.length} onClick={() => handleDownloadZip(category)} />
                        </div>
                    </div>

                    {resultsBlock}
                </>
            )}

            {view === 'class' && (
                <>
                    <div className="lr-filter-card p-mt-3">
                        <div className="lr-filter-grid">
                            <div className="custom-inputs">
                                <label>Academic Year</label>
                                <Dropdown className="p-w-100 p-mt-1" value={year} options={yearList} onChange={(e) => setYear(e.value)} placeholder="Select Year" filter showClear />
                            </div>
                            <div className="custom-inputs">
                                <label>Course/Class</label>
                                <Dropdown className="p-w-100 p-mt-1" value={course} options={classList} onChange={(e) => handleClassView(e.value)} placeholder="Select Course/Class" filter showClear />
                            </div>
                        </div>
                        <div className="lr-actions">
                            <Button label="Download All" icon="pi pi-download" className="btn-text-action-primary lr-download"
                                disabled={!results.length} onClick={() => handleDownloadZip(null)} />
                        </div>
                    </div>

                    {resultsBlock}
                </>
            )}
        </div>
    );
};

export default LearningResources;
